import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Grid,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Card,
  CardContent,
  Box,
  Alert,
  CircularProgress,
  Snackbar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import GroupIcon from '@mui/icons-material/Group';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5001';

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

function PutawaySignOn() {

  // State
  const [vendors, setVendors] = useState([]);
  const [activeSessions, setActiveSessions] = useState([]);
  const [todayCompleted, setTodayCompleted] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form state
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [laborCount, setLaborCount] = useState('');
  const [poNumber, setPoNumber] = useState('');
  const [addingVendor, setAddingVendor] = useState(false);
  const [newVendorName, setNewVendorName] = useState('');

  // Dialog state
  const [stopDialogOpen, setStopDialogOpen] = useState(false);
  const [sessionToStop, setSessionToStop] = useState(null);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Timer state - used to trigger re-renders every second
  const [now, setNow] = useState(new Date());

  // Fetch vendors
  const fetchVendors = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/putaway/vendors`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setVendors(data);
      }
    } catch (error) {
      console.error('Error fetching vendors:', error);
    }
  }, []);

  // Fetch active sessions
  const fetchActiveSessions = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/putaway/sessions/active`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setActiveSessions(data);
      }
    } catch (error) {
      console.error('Error fetching active sessions:', error);
    }
  }, []);

  // Fetch today's completed sessions
  const fetchTodayCompleted = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/putaway/today`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setTodayCompleted(data);
      }
    } catch (error) {
      console.error('Error fetching today completed:', error);
    }
  }, []);

  // Initial load
  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([fetchVendors(), fetchActiveSessions(), fetchTodayCompleted()]);
      setLoading(false);
    };
    loadAll();
  }, [fetchVendors, fetchActiveSessions, fetchTodayCompleted]);

  // Timer update interval - always running so the display stays current.
  // formatElapsed uses Date.now() directly, so this just triggers re-renders.
  useEffect(() => {
    const interval = setInterval(() => {
      console.log('⏱️ TICK', new Date().toISOString());
      setNow(new Date());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Format elapsed time as HH:MM:SS
  const formatElapsed = (startTime) => {
    console.log('⏱️ formatElapsed called, start_time:', startTime, 'diff:', Date.now() - new Date(startTime).getTime());
    // Append 'Z' if no timezone indicator so the browser parses it as UTC
    const utcString = startTime && !startTime.endsWith('Z') ? startTime + 'Z' : startTime;
    const start = new Date(utcString);

    // Use Date.now() directly — the 'now' state may be stale if there were no
    // active sessions for a while (interval only runs when activeSessions.length > 0).
    // 'now' state is still used to trigger re-renders every second.
    const diffMs = Date.now() - start.getTime();
    const totalSeconds = Math.max(0, Math.floor(diffMs / 1000));

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Format duration as Xh Ym or Xs for short sessions
  const formatDuration = (totalSeconds) => {
    if (totalSeconds == null || totalSeconds === '') return '-';
    const abs = Math.abs(totalSeconds);
    const hours = Math.floor(abs / 3600);
    const minutes = Math.floor((abs % 3600) / 60);
    const seconds = abs % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    if (minutes > 0) {
      return `${minutes}m`;
    }
    return `${seconds}s`;
  };

  // Format time
  const formatTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  // Add new vendor
  const handleAddVendor = async () => {
    if (!newVendorName.trim()) return;

    try {
      const response = await fetch(`${API_BASE}/api/putaway/vendors`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ name: newVendorName.trim() })
      });

      if (response.ok) {
        const vendor = await response.json();
        setVendors(prev => [...prev, vendor].sort((a, b) => a.name.localeCompare(b.name)));
        setSelectedVendor(vendor);
        setNewVendorName('');
        setAddingVendor(false);
        setSnackbar({ open: true, message: `Vendor "${vendor.name}" added`, severity: 'success' });
      } else {
        const error = await response.json();
        setSnackbar({ open: true, message: error.error || 'Failed to add vendor', severity: 'error' });
      }
    } catch (error) {
      console.error('Error adding vendor:', error);
      setSnackbar({ open: true, message: 'Failed to add vendor', severity: 'error' });
    }
  };

  // Start timer
  const handleStartTimer = async () => {
    if (!selectedVendor || !laborCount || parseFloat(laborCount) < 0.5) return;

    try {
      const response = await fetch(`${API_BASE}/api/putaway/sessions`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          vendor_id: selectedVendor.id,
          labor_count: parseFloat(laborCount),
          po_number: poNumber.trim() || null
        })
      });

      if (response.ok) {
        const session = await response.json();
        setActiveSessions(prev => [...prev, session]);
        setSelectedVendor(null);
        setLaborCount('');
        setPoNumber('');
        setSnackbar({ open: true, message: `Timer started for ${session.vendor_name}`, severity: 'success' });
      } else {
        const error = await response.json();
        setSnackbar({ open: true, message: error.error || 'Failed to start timer', severity: 'error' });
      }
    } catch (error) {
      console.error('Error starting timer:', error);
      setSnackbar({ open: true, message: 'Failed to start timer', severity: 'error' });
    }
  };

  // Stop timer
  const handleStopTimer = async () => {
    if (!sessionToStop) return;

    try {
      const response = await fetch(`${API_BASE}/api/putaway/sessions/${sessionToStop.id}/complete`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const completedSession = await response.json();
        setActiveSessions(prev => prev.filter(s => s.id !== sessionToStop.id));
        setTodayCompleted(prev => [completedSession, ...prev]);
        setSnackbar({
          open: true,
          message: `${sessionToStop.vendor_name} completed - ${formatDuration(completedSession.total_seconds)}`,
          severity: 'success'
        });
      } else {
        const error = await response.json();
        setSnackbar({ open: true, message: error.error || 'Failed to stop timer', severity: 'error' });
      }
    } catch (error) {
      console.error('Error stopping timer:', error);
      setSnackbar({ open: true, message: 'Failed to stop timer', severity: 'error' });
    } finally {
      setStopDialogOpen(false);
      setSessionToStop(null);
    }
  };

  // Check if vendor already has active timer
  const vendorHasActiveTimer = (vendorId) => {
    return activeSessions.some(s => s.vendor_id === vendorId);
  };

  // Refresh all data
  const handleRefresh = async () => {
    await Promise.all([fetchVendors(), fetchActiveSessions(), fetchTodayCompleted()]);
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <LocalShippingIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" fontWeight="bold">
              Putaway Sign On
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Track vendor putaway times
            </Typography>
          </Box>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
        >
          Refresh
        </Button>
      </Box>

      {/* Start New Putaway Section */}
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
          Start New Putaway
        </Typography>

        {/* Add New Vendor inline form */}
        {addingVendor && (
          <Box sx={{ display: 'flex', gap: 1, mb: 2, alignItems: 'center' }}>
            <TextField
              label="New Vendor Name"
              value={newVendorName}
              onChange={(e) => setNewVendorName(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAddVendor()}
              autoFocus
              size="small"
              sx={{ minWidth: 250 }}
            />
            <Button variant="contained" onClick={handleAddVendor} disabled={!newVendorName.trim()}>
              Save
            </Button>
            <Button variant="outlined" onClick={() => { setAddingVendor(false); setNewVendorName(''); }}>
              Cancel
            </Button>
          </Box>
        )}

        <Grid container spacing={2} alignItems="flex-end">
          {/* Vendor Dropdown */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Select Vendor</InputLabel>
              <Select
                value={selectedVendor?.id || ''}
                onChange={(e) => {
                  if (e.target.value === 'add_new') {
                    setAddingVendor(true);
                    setSelectedVendor(null);
                  } else {
                    const vendor = vendors.find(v => v.id === e.target.value);
                    setSelectedVendor(vendor || null);
                  }
                }}
                label="Select Vendor"
              >
                {vendors.map((vendor) => (
                  <MenuItem key={vendor.id} value={vendor.id}>
                    {vendor.name}
                  </MenuItem>
                ))}
                <Divider />
                <MenuItem value="add_new" sx={{ color: 'primary.main', fontWeight: 500 }}>
                  <AddIcon fontSize="small" sx={{ mr: 1 }} />
                  Add New Vendor
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Labor Count */}
          <Grid item xs={6} sm={6} md={3}>
            <TextField
              fullWidth
              label="Labor Count"
              type="number"
              value={laborCount}
              onChange={(e) => setLaborCount(e.target.value)}
              inputProps={{ min: 0.5, step: 0.5 }}
              size="small"
            />
          </Grid>

          {/* PO Number */}
          <Grid item xs={6} sm={6} md={3}>
            <TextField
              fullWidth
              label="PO Number (Optional)"
              value={poNumber}
              onChange={(e) => setPoNumber(e.target.value)}
              size="small"
            />
          </Grid>

          {/* Start Button */}
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              size="large"
              startIcon={<PlayArrowIcon />}
              onClick={handleStartTimer}
              disabled={!selectedVendor || !laborCount || parseFloat(laborCount) < 0.5 || vendorHasActiveTimer(selectedVendor?.id)}
            >
              Start Timer
            </Button>
            {selectedVendor && vendorHasActiveTimer(selectedVendor.id) && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                This vendor already has an active timer
              </Typography>
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* Active Timers Section */}
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <AccessTimeIcon color="primary" />
          <Typography variant="h6" fontWeight="bold">
            Active Timers ({activeSessions.length})
          </Typography>
        </Box>

        {console.log('⏱️ activeSessions:', activeSessions)}
        {activeSessions.length === 0 ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            No active timers. Start a new putaway above.
          </Alert>
        ) : (
          <Grid container spacing={2}>
            {activeSessions.map((session) => (
              <Grid item xs={12} sm={6} md={4} key={session.id}>
                <Card
                  elevation={2}
                  sx={{
                    background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                    border: '2px solid #1976d2'
                  }}
                >
                  <CardContent>
                    <Typography variant="h6" fontWeight="bold" color="primary.dark">
                      {session.vendor_name}
                    </Typography>
                    {session.po_number && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        PO: {session.po_number}
                      </Typography>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                      <GroupIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {parseFloat(session.labor_count)} {parseFloat(session.labor_count) === 1 ? 'person' : 'people'}
                      </Typography>
                    </Box>
                    <Typography
                      variant="h3"
                      fontWeight="bold"
                      sx={{
                        fontFamily: 'monospace',
                        textAlign: 'center',
                        color: '#1565c0',
                        my: 2
                      }}
                    >
                      {formatElapsed(session.start_time)}
                    </Typography>
                    <Button
                      fullWidth
                      variant="contained"
                      color="error"
                      startIcon={<StopIcon />}
                      onClick={() => {
                        setSessionToStop(session);
                        setStopDialogOpen(true);
                      }}
                    >
                      Stop Timer
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>

      {/* Today's Completed Putaways */}
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
          Today's Completed Putaways ({todayCompleted.length})
        </Typography>

        {todayCompleted.length === 0 ? (
          <Alert severity="info">
            No completed putaways today.
          </Alert>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Vendor</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>PO Number</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Labor</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Labor Time</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Completed</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {todayCompleted.map((session) => (
                  <TableRow key={session.id} hover>
                    <TableCell sx={{ fontWeight: 500 }}>{session.vendor_name}</TableCell>
                    <TableCell>{session.po_number || '-'}</TableCell>
                    <TableCell>{parseFloat(session.labor_count)}</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>
                      {formatDuration(session.total_seconds)}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>
                      {formatDuration(parseFloat(session.labor_count) * session.total_seconds)}
                    </TableCell>
                    <TableCell>{formatTime(session.end_time)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Stop Confirmation Dialog */}
      <Dialog open={stopDialogOpen} onClose={() => { setStopDialogOpen(false); setSessionToStop(null); }}>
        <DialogTitle>Stop Timer?</DialogTitle>
        <DialogContent>
          <Typography>
            Stop the timer for <strong>{sessionToStop?.vendor_name}</strong>?
          </Typography>
          {sessionToStop && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Current time: {formatElapsed(sessionToStop.start_time)}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setStopDialogOpen(false); setSessionToStop(null); }}>
            Cancel
          </Button>
          <Button onClick={handleStopTimer} variant="contained" color="error" startIcon={<StopIcon />}>
            Stop Timer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default PutawaySignOn;
